/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {TypeaheadHelperService} from '../../../../shared/services/typeahead-helper.service';
import {
    AbstractControl,
    ValidatorFn
} from '@angular/forms';
import {InputTextComponent} from '../../../../shared/input/input-text/input-text.component';
import {StatefulComponent} from '../../../../shared/stateful/stateful.component';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';
import {PartNumberChangesAdditionalFullLotNumberModel} from '../../../models/part-number-changes-additional-full-lot-number.model';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';

@Component({
    selector: 'app-pnc-additional-lot-number',
    templateUrl: './pnc-additional-lot-number.component.html',
    styleUrls: ['./pnc-additional-lot-number.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class PncAdditionalLotNumberComponent
        extends StatefulComponent<PartNumberChangesAdditionalFullLotNumberModel>
        implements OnInit, OnChanges {
    @Output() newLotNumberChange = new EventEmitter<PartNumberChangesAdditionalFullLotNumberModel>();
    @ViewChild('fullLotNumberInput', {static: true}) fullLotNumberInput: InputTextComponent;
    @ViewChild('quantityInput', {static: true}) quantityInput: InputTextComponent;
    @Output() deleteClick = new EventEmitter<void>();

    public readonly fullLotNumberCypressTag = 'pnc-additional-full-lot-number';
    public readonly quantityCypressTag = 'pnc-additional-quantity';
    public readonly STYLE_TAG = 'pnc-additional-lot-number container-divided';
    public readonly LABEL_ADDITIONAL = 'Additional Full LOT No.';
    public readonly LABEL_NEW = 'Full LOT No';
    /* General component styling inputs */
    @Input() isDetailMode: boolean;
    @Input() isSolvedStatus: boolean;
    @Input() isDetailSolvedTrial: boolean;
    @Input() isInViewMode: boolean;
    @Input() additionalIndex?: number;
    @Input() isSolutionAccepted: boolean;
    /* Full LOT number field inputs */
    @Input() fullLotNumber: string;
    @Input() fullLotNumberValidators: ValidatorFn[];
    @Input() fullLotNumberId: string;
    /* Quantity field inputs */
    @Input() quantityValidators: ValidatorFn[];
    @Input() quantity: number;
    WarningsEnum = WarningsEnum;

    public lotNumberInputMessages = new Map<WarningsEnum, string[]>();
    public quantityInputMessages = new Map<WarningsEnum, string[]>();

    constructor(public typeahead: TypeaheadHelperService,
                private inputMessagesService: InputMessagesService) {
        super();
    }

    get lotNumberLabel(): string {
        return this.additionalIndex != null ? `${this.LABEL_ADDITIONAL} ${this.additionalIndex}` : this.LABEL_NEW;
    }

    get fullLotNumberBaseCypressTag(): string {
        return this.additionalIndex != null ? `${this.fullLotNumberCypressTag}-${this.additionalIndex}` : this.fullLotNumberCypressTag;
    };

    get quantityBaseCypressTag(): string {
        return this.additionalIndex != null ? `${this.quantityCypressTag}-${this.additionalIndex}` : this.quantityCypressTag;
    };

    ngOnInit(): void {
        this.storeState({fullLotNumber: this.fullLotNumber, quantity: this.quantity, id: this.fullLotNumberId});
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['fullLotNumber'] && !changes['fullLotNumber'].firstChange
                && changes['fullLotNumber'].previousValue !== changes['fullLotNumber'].currentValue) {
            this.fullLotNumberInput.setValue(changes['fullLotNumber'].currentValue);
        }
    }

    delete(): void {
        this.deleteClick.emit();
    }

    storeState(state?: Partial<PartNumberChangesAdditionalFullLotNumberModel>): void {
        super.storeState(state);
        super.storeState({
            isValid: this.fullLotNumberInput?.isValid && state?.fullLotNumber != null && this.quantityInput?.isValid
        });
        this.newLotNumberChange.emit(this.state);
    }

    lotNumberChange(event: string): void {
        this.storeState({
            fullLotNumber: event ? event.toUpperCase() : null,
            quantity: this.state.quantity
        });
    }

    quantityChange(event: string): void {
        this.storeState({
            fullLotNumber: this.state.fullLotNumber,
            quantity: (event === '' || event == null) ? null : parseInt(event, 10)
        });
    }

    manageLotNumberMessages(lotNumber: AbstractControl): void {
        this.inputMessagesService.mandatoryWarnings(lotNumber,
                this.lotNumberInputMessages,
                MandatoryMessageTypeEnum.CREATE);
        this.inputMessagesService.invalidInputValueError(lotNumber,
                this.lotNumberInputMessages,
                'Full LOT number. e.g. E4R5T7896');

        this.lotNumberInputMessages = new Map(this.lotNumberInputMessages);
    }

    manageQuantityMessages(quantity: AbstractControl): void {
        this.inputMessagesService.invalidInputValueError(quantity,
                this.quantityInputMessages,
                'Quantity. e.g. 1');

        this.quantityInputMessages = new Map(this.quantityInputMessages);
    }

    quantityDisplayFormatted(isViewMode: boolean, quantity: number): string {
        let value = '-';

        if (quantity != null || !isViewMode) {
            value = quantity?.toString();
        }

        return value;
    }
}


